import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { toCurrency } from 'src/helpers/currency/toCurrency';
import { CartGrade, CartProduct } from 'src/model/CartProduct';
import { Grade } from 'src/model/Grade';
import { Product } from 'src/model/Product';
import Client from 'src/model/User';
import { setLoading } from 'src/redux/loading/loadingSlice';
import { api } from 'src/services/api';
import { ProductUI } from './ui';
import { trackTikTokEvent } from 'src/pixel/tiktok';

interface Params {
  id: string;
}

interface ProductAddCart {
  id: string;
  product: CartProduct;
  quantity: number;
  totalValue: number;
}

export interface SelectGrade {
  color: string;
  id: string;
}

export interface SelectSize {
  id: string;
  size: string;
}

export const ProductScreen: React.FC = (): JSX.Element => {
  const { id } = useParams<Params>();
  const [products, setProdutos] = useState<Product>();
  const [quantity, setQuantity] = useState<number>(1);
  const dispatch = useDispatch();
  const [users, setUsers] = useState<Client>({} as Client);
  const [shippingCost, setShippingCost] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<SelectGrade>(
    {} as SelectGrade
  );
  const [selectedSize, setSelectedSize] = useState<SelectSize>(
    {} as SelectSize
  );
  const [zoomStyle, setZoomStyle] = useState<React.CSSProperties>({});
  const history = useHistory();

  useEffect(() => {
    if (products && products.grade.length > 0) {
      const firstGrade = products.grade[0];

      if (firstGrade.color === 'UNICO' && firstGrade.name === null) {
        if (firstGrade.id) {
          setSelectedColor({
            color: firstGrade.color,
            id: firstGrade.id,
          });
        }

        if (firstGrade.gradeSizes.length > 0) {
          const firstSize = firstGrade.gradeSizes[0];
          if (firstSize.id && firstSize.size) {
            setSelectedSize({
              id: firstSize.id,
              size: firstSize.size,
            });
          }
        }

        if (firstGrade.image) {
          setSelectedImage(
            `${process.env.REACT_APP_BASE_URL}/uploads/${firstGrade.image}`
          );
        }
      }
    }
  }, [products]);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomStyle({
      backgroundPosition: `${x}% ${y}%`,
      backgroundSize: '200%', // Ajuste o nível de zoom aqui
    });
  };

  const handleMouseLeave = () => {
    setZoomStyle({});
  };

  const getProduct = async (): Promise<void> => {
    dispatch(setLoading(true));
    try {
      const response = await api.get<Product>(`/product/one/${id}`);
      console.log(response.data);
      if (response.status === 200) {
        const data = response.data;
        const addImagesGrade: string[] = [];
        data.grade.forEach((grade) => {
          if (grade.image) {
            addImagesGrade.push(
              `${process.env.REACT_APP_BASE_URL}/uploads/` + grade.image
            );
          }
        });
        data.imagens = [...data.imagens];
        setProdutos(data);
        setSelectedImage(data.imagens[0]);
      }
    } catch (error) {
      console.error('Erro ao buscar categoria:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleColorSelect = (color?: string, image?: string, id?: string) => {
    if (color && id) {
      setSelectedColor({ color, id });
    }
    setSelectedSize({} as SelectSize);
    if (image) {
      setSelectedImage(image);
    }

    if (color && products) {
      const grade = products.grade.find((g) => g.color === color);
      if (grade && grade.gradeSizes.length === 1) {
        const singleSize = grade.gradeSizes[0];
        if (singleSize.id && singleSize.size) {
          setSelectedSize({ id: singleSize.id, size: singleSize.size });
        }
      }
    }
  };

  const handleSizeSelect = (size: string, id: string): void => {
    setSelectedSize({ id, size });
  };
  const handleQuantityChange = (increment: boolean) => {
    setQuantity((prev) => Math.max(1, prev + (increment ? 1 : -1)));
  };

  const getPrice = (
    grade: Grade[],
    selectedColor: string,
    selectedSize: string
  ): number => {
    let price = 0;
    for (let i = 0; i < grade.length; i++) {
      for (let s = 0; s < grade[i].gradeSizes.length; s++) {
        if (
          selectedColor === grade[i].color &&
          selectedSize === grade[i].gradeSizes[s].size
        ) {
          price = grade[i].gradeSizes[s].price.price;
        }
      }
    }
    return price;
  };

  const handleAddCart = async (): Promise<void> => {
    if (!products || quantity <= 0) {
      console.log('Produto ou quantidade inválidos');
      return;
    }

    let isGradeSelect = true;

    products.grade.forEach((grade) => {
      if (grade.name === 'UNICO' && grade.color === 'UNICO') {
        isGradeSelect = false;
      }
    });

    if (isGradeSelect && !selectedColor.color) {
      toast.error('Selecione uma cor');
      return;
    }

    if (isGradeSelect && !selectedSize.size) {
      toast.error('Selecione um tamanho');
      return;
    }

    let selectedStock: number | null = null;

    if (isGradeSelect) {
      const gradeFound = products.grade.find(
        (g) => g.color === selectedColor.color
      );
      if (gradeFound) {
        const sizeFound = gradeFound.gradeSizes.find(
          (s) => s.size.toLowerCase() === selectedSize.size.toLowerCase()
        );
        if (sizeFound) {
          selectedStock = Number(sizeFound.stock);
        }
      }
    } else {
      const gradeUnique = products.grade[0];
      if (gradeUnique && gradeUnique.gradeSizes.length > 0) {
        selectedStock = Number(gradeUnique.gradeSizes[0].stock);
      }
    }

    if (selectedStock !== null && selectedStock < quantity) {
      toast.error('Não há estoque suficiente para a quantidade informada');
      return;
    }

    try {
      dispatch(setLoading(true));

      const isSeller = users.type === 'SELLER' || users.type === 'ADMIN';
      const isOwnProduct = products.storeSeller?.id === users.sellerId;
      console.log(products.storeSeller?.id);
      console.log(users.sellerId);
      if (isSeller && isOwnProduct) {
        toast.error('Você não pode comprar seus próprios produtos');
        return;
      }

      const price = getPrice(
        products.grade,
        selectedColor.color,
        selectedSize.size
      );

      const CartGrade: CartGrade = {
        id: selectedColor.id,
        gradeSizeId: selectedSize.id,
        name: isGradeSelect ? '' : 'UNICO',
        color: isGradeSelect ? selectedColor.color : 'UNICO',
        size: isGradeSelect ? selectedSize.size : 'unico',
        price,
      };

      const CartPoduct: CartProduct = {
        id: products.id,
        name: products.name,
        quantity,
        totalValue: price * quantity,
        grade: CartGrade,
      };

      const payload: ProductAddCart = {
        id: isGradeSelect ? selectedColor.id : '',
        product: CartPoduct,
        quantity,
        totalValue: price * quantity,
      };

      const response = await api.post('/order/add-carts', payload);

      if (response.status === 201 || response.status === 200) {
        toast.success('Produto adicionado ao carrinho');
        trackTikTokEvent('AddToCart', {
          content_id: products.id,
          content_name: products.name,
          content_type: 'product',
          quantity: quantity,
          value: price * quantity,
          currency: 'BRL',
          color: CartGrade.color,
          size: CartGrade.size,
        });
      } else {
        toast.error('Erro ao adicionar produto ao carrinho');
      }
    } catch (error) {
      console.error('Erro ao adicionar produto ao carrinho:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const getUser = async (): Promise<void> => {
    dispatch(setLoading(true));
    try {
      const response = await api.get<Client>('/user/findeone');
      console.log(response.data);
      if (response.status === 200) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar usuarios:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleShippingCalc = async (
    altura: string,
    largura: string,
    profundidade: string,
    peso: string
  ) => {
    try {
      console.log('Entrou handleShippingCalc');
      const payload = { altura, largura, profundidade, peso };

      const response = await api.post('/product/calc-frete', payload);

      setShippingCost(toCurrency(response.data.frete));
    } catch (error) {
      console.error('Erro ao calcular frete:', error);
      setShippingCost('Erro ao calcular o frete.');
    }
  };

  const handleChat = async (sellerId: string): Promise<void> => {
    console.log('Iniciando chat com o vendedor:', sellerId);
    if (!sellerId) return;
    try {
      const response = await api.post('/chat', sellerId);
      console.log(response.status);

      toast.success('Chat criado com sucesso!');
      history.push('/user-profile?tab=chat');
    } catch (error: any) {
      if (error.response?.status === 400) {
        toast.info('Você já possui um chat ativo com esta loja.');
        //window.location.href = '/user-profile?tab=chat';
      } else {
        console.error('Erro ao criar o chat:', error);
        toast.error('Erro ao iniciar o chat. Tente novamente.');
      }
    }
  };

  useEffect(() => {
    const userAddress = users.address?.map(
      (user) => user.city === products?.storeSeller.city
    );

    if (userAddress) {
      handleShippingCalc(
        products?.dimensions.height ?? '',
        products?.dimensions.width ?? '',
        products?.dimensions.depth ?? '',
        products?.dimensions.weight ?? ''
      );
    }
  }, [users.address, products?.dimensions, products?.storeSeller.city]);

  useEffect(() => {
    getProduct();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getUser();
  }, []);

  return (
    <>
      {products ? (
        <ProductUI
          products={products}
          onAddCart={handleAddCart}
          quantity={quantity}
          quantityChange={handleQuantityChange}
          user={users}
          shippingCost={shippingCost}
          selectedImage={selectedImage}
          selectedColor={selectedColor}
          selectedSize={selectedSize}
          handleColorSelect={handleColorSelect}
          handleSizeSelect={handleSizeSelect}
          zoomStyle={zoomStyle}
          handleMouseMove={handleMouseMove}
          handleMouseLeave={handleMouseLeave}
          onChat={handleChat}
        />
      ) : (
        <p>Carregando produto...</p>
      )}
    </>
  );
};
